import type { ITerm } from "store/managementStore";
import type { Binary } from "types";

export type CreatedTermListEntry = {
    id: number;
    titleId: number | null;
    workId: number | null;
    beneficiarId: number;
    title: string;
    author: string;
    firstname: string | null;
    lastname: string;
    beneficiaryAvatarUrl: string | null;
    resourceAvatarUrl: string | null;
    representative: string;
    representativeId: number | null;
    endDate: string | null;
    reference: string | null;
    periodTypeId: number | null;
    compensationGroupId: number | null;
    compensate: number;
    isbn: string | null;
    releaseDate: string | null;
    isTitlePublishedBeforePeriod: Binary;
    coveredTitleIds: number[];
    computeFromRepresentative: number;
    originalPeriod: number;
};

export type CreatedTermSource = {
    id: number;
    titleId: number | null;
    workId: number | null;
    beneficiarId: number;
    title: string;
    authorSurname: string;
    authorName: string | null;
    beneficiaryAvatarUrl: string | null;
    resourceAvatarUrl: string | null;
    representativeSurname?: string | null;
    representativeName?: string | null;
    representativeId: number | null;
    computeFromRepresentative: boolean;
    endDate: string | null;
    reference?: string | null;
    periodTypeId?: number | null;
    compensationGroupId?: number | null;
    compensate: number;
    isbn: string | null;
    releaseDate: string | null;
    isTitlePublishedBeforePeriod: Binary;
    coveredTitleIds: number[];
    originalPeriod: number;
};

const personLabel = (surname?: string | null, name?: string | null) =>
    [surname?.trim().toUpperCase(), name?.trim()].filter((part) => part).join(" ");

export const buildCreatedTermListEntry = (source: CreatedTermSource): CreatedTermListEntry => {
    const hasRepresentative = source.representativeId != null && source.representativeId > 0;
    return {
        id: source.id,
        titleId: source.titleId,
        workId: source.workId,
        beneficiarId: source.beneficiarId,
        title: source.title,
        author: personLabel(source.authorSurname, source.authorName),
        firstname: source.authorName,
        lastname: source.authorSurname,
        beneficiaryAvatarUrl: source.beneficiaryAvatarUrl,
        resourceAvatarUrl: source.resourceAvatarUrl,
        representative: hasRepresentative ? personLabel(source.representativeSurname, source.representativeName) : "",
        representativeId: hasRepresentative ? source.representativeId : null,
        endDate: source.endDate,
        reference: source.reference ?? null,
        periodTypeId: source.periodTypeId ?? null,
        compensationGroupId: source.compensationGroupId ?? null,
        compensate: source.compensate,
        isbn: source.isbn,
        releaseDate: source.releaseDate,
        isTitlePublishedBeforePeriod: source.isTitlePublishedBeforePeriod,
        coveredTitleIds: source.coveredTitleIds,
        computeFromRepresentative: source.computeFromRepresentative ? 1 : 0,
        originalPeriod: source.originalPeriod,
    };
};

const toListTerm = (entry: CreatedTermListEntry, avatar: "beneficiary" | "resource"): ITerm => ({
    id: entry.id,
    termId: entry.id,
    title: entry.title,
    titleId: entry.titleId,
    workId: entry.workId,
    author: entry.author,
    firstname: entry.firstname ?? undefined,
    lastname: entry.lastname,
    beneficiarId: entry.beneficiarId,
    representative: entry.representative,
    representativeId: entry.representativeId ?? undefined,
    amount: "0",
    balancePrev: "0",
    isbn: entry.isbn,
    quantity: "0",
    releaseDate: entry.releaseDate ?? "",
    creationDate: new Date().toISOString(),
    endDate: entry.endDate ? new Date(`${entry.endDate}T00:00:00.000Z`) : null,
    additionalRights: "0",
    collectiveManagement: "0",
    hasPostCloseCollectiveManagementSales: 0,
    audiovisualAdaption: "0",
    other: "0",
    grossDuty: "0",
    isClosed: 0,
    hasEnded: false,
    balance: "0",
    initialAdvance: 0,
    advancePayment: 0,
    channelData: [],
    notesCount: 0,
    termExistsOnClosedPeriod: 0,
    precount: 0,
    reference: entry.reference ?? undefined,
    compensate: entry.compensate,
    avatarUrl: avatar === "beneficiary" ? entry.beneficiaryAvatarUrl : entry.resourceAvatarUrl,
    beneficiaryAvatarUrl: entry.beneficiaryAvatarUrl ?? undefined,
    compensationGroupId: entry.compensationGroupId,
    periodTypeId: entry.periodTypeId,
    advancePaymentId: 0,
    cumulativeRights: 0,
    computeFromRepresentative: entry.computeFromRepresentative,
    originalPeriod: entry.originalPeriod,
    coveredTitleIds: entry.coveredTitleIds,
    isTitlePublishedBeforePeriod: entry.isTitlePublishedBeforePeriod,
    originalTPD: 0,
});

/** Insert a created term into an already loaded list, without reordering the other rows. */
export const insertCreatedTerm = (
    terms: ITerm[],
    entry: CreatedTermListEntry,
    options: { avatar: "beneficiary" | "resource"; sortKey: (term: ITerm) => string },
): ITerm[] => {
    if (terms.some((term) => term.id === entry.id)) return terms;
    const created = toListTerm(entry, options.avatar);
    const value = options.sortKey(created);
    const index = terms.findIndex(
        (term) => options.sortKey(term).localeCompare(value, "fr", { sensitivity: "base" }) > 0,
    );
    if (index < 0) return [...terms, created];
    return [...terms.slice(0, index), created, ...terms.slice(index)];
};
